import { inject, Injectable, Type } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { Breadcrumb } from "../types/breadcrumb";
import { UnsubscribeOnDestroy } from "../classes";

@Injectable({
    "providedIn": "root"
})
export class BreadcrumbService extends UnsubscribeOnDestroy {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    breadcrumbs: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);
    hideBreadcrumbComponent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private breadcrumbMap: { [key: string]: BehaviorSubject<Breadcrumb[]> } = {};
    private componentList: string[] = [];
    private routes: { [key: string]: string } = {};

    constructor() {
        super();
        this.router.events
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter((event) => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                this.componentList = this.generateBreadcrumb(this.route.root);
                this.refreshBreadcrumbs();
            });
    }

    setBreadcrumb(componentKey: string, breadcrumbs: Breadcrumb[]): void {
        this.hideBreadcrumbComponent.next(false);
        this.breadcrumbMap[componentKey] = new BehaviorSubject<Breadcrumb[]>(breadcrumbs);
        this.refreshBreadcrumbs();
    }

    hideBreadcrumb(): void {
        this.hideBreadcrumbComponent.next(true);
    }

    showBreadcrumb(): void {
        this.hideBreadcrumbComponent.next(false);
    }

    private refreshBreadcrumbs(): void {
        const breadcrumbs: Breadcrumb[] = [];

        this.componentList.forEach((component, index) => {
            if (this.breadcrumbMap[component]) {
                this.breadcrumbMap[component].getValue().forEach((breadcrumb) => {
                    if (breadcrumb.clickable) {
                        breadcrumb.route = this.routes[component];
                    }

                    breadcrumbs.push({
                        "title": breadcrumb.title,
                        "route": breadcrumb.route,
                        "clickable": breadcrumb.clickable && index != this.componentList.length - 1
                    });
                });
            }
        });

        this.breadcrumbs.next(breadcrumbs);
    }

    private generateBreadcrumb(route: ActivatedRoute, path: string = "", components: string[] = []): string[] {
        const child: ActivatedRoute = route.children.find((child) => child.outlet == "primary");

        if (child) {
            if (child.snapshot.url.length > 0) {
                path += "/" + child.snapshot.url.map((u) => u.path).join("/");
            }

            if (child.component as Type<any>) {
                const component = child.component as Type<any>;
                if (component.name) {
                    components.push(component.name);
                    this.routes[component.name] = path;
                    if (component.prototype.breadcrumbs) {
                        this.breadcrumbMap[component.name] = new BehaviorSubject<Breadcrumb[]>(component.prototype.breadcrumbs);
                    }
                }
            }

            this.generateBreadcrumb(child, path, components);
        }
        return components;
    }
}
